import React, { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import apiClient from '../api/client';
import { useLanguage } from '../context/LanguageContext';
import { getPasswordStrength, isStrongPassword } from '../utils/passwordValidation';
import './Auth.css';

function ResetPassword() {
  const navigate = useNavigate();
  const { token } = useParams();
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const passwordStrength = getPasswordStrength(formData.password);
  const isPasswordValid = isStrongPassword(formData.password);
  const passwordsMatch = formData.password === formData.confirmPassword && formData.confirmPassword.length > 0;
  const canSubmit = isPasswordValid && passwordsMatch;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError(t('passwordMismatch'));
      return;
    }

    if (!isStrongPassword(formData.password)) {
      setError(t('passwordRequirements'));
      return;
    }

    setLoading(true);

    try {
      await apiClient.post('/auth/reset-password', {
        token,
        password: formData.password,
      });
      navigate('/login', { state: { message: t('success') } });
    } catch (err) {
      setError(err.response?.data?.error || t('tryAgainLater'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page container">
      <div className="auth-card">
        <h1>{t('resetPasswordTitle')}</h1>
        <p className="auth-subtext">{t('newPassword')}</p>

        {error && <div className="error">{error}</div>}

        <form onSubmit={handleSubmit}>
          <input
            type="password"
            placeholder={t('newPassword')}
            value={formData.password}
            onChange={(e) => setFormData({ ...formData, password: e.target.value })}
            required
            minLength={8}
            className="input"
          />
          {formData.password && (
            <div className="password-requirements" style={{ marginTop: 8, fontSize: 13, color: '#555' }}>
              <div style={{ marginBottom: 4, fontWeight: 600 }}>{t('passwordRequirements')}:</div>
              <div className={passwordStrength.hasMinLength ? 'req-met' : 'req-unmet'}>• {t('minChars')}</div>
              <div className={passwordStrength.hasUpperCase ? 'req-met' : 'req-unmet'}>• {t('oneUpperCase')}</div>
              <div className={passwordStrength.hasLowerCase ? 'req-met' : 'req-unmet'}>• {t('oneLowerCase')}</div>
              <div className={passwordStrength.hasNumber ? 'req-met' : 'req-unmet'}>• {t('oneNumber')}</div>
              <div className={passwordStrength.hasSymbol ? 'req-met' : 'req-unmet'}>• {t('oneSymbol')}</div>
            </div>
          )}
          <input
            type="password"
            placeholder={t('confirmPassword')}
            value={formData.confirmPassword}
            onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
            required
            minLength={8}
            className="input"
          />
          <button type="submit" className="btn btn-primary" disabled={loading || !canSubmit}>
            {loading ? t('sending') : t('resetPassword')}
          </button>
        </form>

        <div className="auth-divider">
          <span className="divider-line"></span>
          <span className="divider-text">{t('or')}</span>
          <span className="divider-line"></span>
        </div>

        <Link to="/login" className="btn btn-secondary">{t('back')}</Link>
      </div>
    </div>
  );
}

export default ResetPassword;
